import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { addDays, todayInTashkent, type DateOnly } from '@cr/shared';
import { api } from '@/lib/api';
import { Card, DateInput, Field, Select, type Localized } from '@/components/ui';

interface DayLoad {
  date: DateOnly;
  reserved: number;
  free: number;
}

interface LoadRow {
  variantId: string;
  sku: string;
  product: { id: string; name: Localized; code: string };
  size: string | null;
  color: { name: Localized; hex: string } | null;
  quantityTotal: number;
  days: DayLoad[];
}

interface CategoryNode {
  id: string;
  level: number;
  name: Localized;
  children: CategoryNode[];
}

const WEEKDAYS = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

/** Цвет ячейки: свободно всё → серый, частично занято → янтарный, ноль → красный. */
function cellStyle(day: DayLoad, total: number): string {
  if (day.free <= 0) return 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300';
  if (day.free < total) return 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300';
  return 'text-neutral-400';
}

export default function AvailabilityPage() {
  const today = todayInTashkent();
  const [from, setFrom] = useState<DateOnly>(today);
  const [days, setDays] = useState(21);
  const [categoryId, setCategoryId] = useState('');

  const to = useMemo(() => addDays(from, days - 1), [from, days]);

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => (await api.get<CategoryNode[]>('/admin/categories')).data,
  });

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['availability-load', from, to, categoryId],
    queryFn: async () =>
      (
        await api.get<LoadRow[]>('/admin/availability/load', {
          params: { from, to, categoryId: categoryId || undefined },
        })
      ).data,
    placeholderData: keepPreviousData,
  });

  const dates = rows[0]?.days ?? [];

  const flat = (nodes: CategoryNode[]): CategoryNode[] =>
    nodes.flatMap((n) => [n, ...flat(n.children)]);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Календарь загрузки</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Свободные единицы по дням. Занятость включает буфер на чистку после возврата.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <Field label="С даты">
          <DateInput value={from} onChange={(e) => setFrom(e.target.value as DateOnly)} />
        </Field>

        <Field label="Период">
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>7 дней</option>
            <option value={14}>14 дней</option>
            <option value={21}>21 день</option>
            <option value={31}>31 день</option>
          </Select>
        </Field>

        <Field label="Категория">
          <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            <option value="">Все</option>
            {flat(categories).map((c) => (
              <option key={c.id} value={c.id}>
                {'— '.repeat(c.level - 1)}
                {c.name.ru}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Card>
        {isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-neutral-500">Нет вариаций — заведите товары и остатки</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="text-sm">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-white p-2 text-left text-xs uppercase text-neutral-500 dark:bg-neutral-950">
                    Вариация
                  </th>
                  <th className="p-2 text-xs uppercase text-neutral-500">Всего</th>
                  {dates.map((day) => {
                    const date = new Date(`${day.date}T00:00:00Z`);
                    const weekend = [0, 6].includes(date.getUTCDay());

                    return (
                      <th
                        key={day.date}
                        className={`p-1 text-center text-xs font-normal ${weekend ? 'text-red-500' : 'text-neutral-500'}`}
                      >
                        <div>{day.date.slice(8)}</div>
                        <div className="text-[10px]">{WEEKDAYS[date.getUTCDay()]}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.variantId}
                    className="border-t border-neutral-100 dark:border-neutral-800"
                  >
                    <td className="sticky left-0 bg-white p-2 whitespace-nowrap dark:bg-neutral-950">
                      <span className="block">{row.product.name.ru}</span>
                      <span className="flex items-center gap-1.5 text-xs text-neutral-400">
                        {row.color && (
                          <span
                            className="size-2.5 rounded-full border border-neutral-300"
                            style={{ background: row.color.hex }}
                          />
                        )}
                        {row.size ?? '—'} · {row.color?.name.ru ?? 'без цвета'}
                      </span>
                    </td>
                    <td className="p-2 text-center text-neutral-500">{row.quantityTotal}</td>
                    {row.days.map((day) => (
                      <td key={day.date} className="p-1">
                        <div
                          className={`rounded px-1.5 py-1 text-center text-xs ${cellStyle(day, row.quantityTotal)}`}
                          title={`${day.date}: занято ${day.reserved} из ${row.quantityTotal}`}
                        >
                          {day.free}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-xs text-neutral-500">
        В ячейке — сколько единиц свободно. Красная означает, что на эту дату товар взять нельзя.
      </p>
    </div>
  );
}
